import { css, FlattenInterpolation, ThemeProps } from 'styled-components';
import colors from '../helpers/colors';
import { ColumnsByBreakpoint } from '../helpers/columns/types';
import { stylePerBreakpoint } from '../helpers/mediaQuery';
import { Theme } from '../theme';
import fluidGridColumns from '../theme/fluidGridColumns';

/**
 * Styles for the "white rectangles" that cover the parent shape to form a chevron.
 */
export const whiteRectangleStyles = (
  chevronTipPositionByColumn: ColumnsByBreakpoint,
  totalWidthInColumns: ColumnsByBreakpoint,
  reverse?: boolean
): FlattenInterpolation<ThemeProps<Theme>> => css`
  content: '';
  position: absolute;
  height: 50vw;
  background: ${colors.neutral08};

  ${stylePerBreakpoint((breakpoint) => {
    const sharpSize = `${
      (chevronTipPositionByColumn[breakpoint] / totalWidthInColumns[breakpoint]) * -100
    }%`;
    const wideSize = `${
      (chevronTipPositionByColumn[breakpoint] / totalWidthInColumns[breakpoint] - 1) * 100
    }%`;
    return css`
      right: ${!reverse ? sharpSize : wideSize};
      left: ${!reverse ? wideSize : sharpSize};
    `;
  })}
`;

export const fullPageWhiteRectangleStyles = (
  chevronTipPositionByColumn: ColumnsByBreakpoint,
  reverse?: boolean
): FlattenInterpolation<ThemeProps<Theme>> => css`
  ${whiteRectangleStyles(chevronTipPositionByColumn, fluidGridColumns, reverse)}

  /* Once we reach the container width for the desktop view, we should use the container width to calculate the position 
     of the chevron tip, so that the tip doesn't rely on the width of the window (which would result in the chevron being 
     skewed far to the right).
  */ 
    ${({ theme }) => {
      if (!theme.layout.desktop.containerWidthPx) {
        return;
      }
      const sharpSize = `${
        (-chevronTipPositionByColumn.desktop / fluidGridColumns.desktop) *
        theme.layout.desktop.containerWidthPx
      }px`;
      const wideSize = `${
        (chevronTipPositionByColumn.desktop / fluidGridColumns.desktop - 1) *
        theme.layout.desktop.containerWidthPx
      }px`;

      return css`
        @media (min-width: ${theme.layout.desktop.containerWidthPx}px) {
          right: ${!reverse ? sharpSize : wideSize};
          left: ${!reverse ? wideSize : sharpSize};
        }
      `;
    }}
`;

/**
 * Padding to ensure the content sits above the main part of the chevron triangle
 */
export const chevronPadding = (
  chevronTipPositionByColumn: number,
  totalNumberColumns: number,
  chevronLeftTriangleWidthToHeightRatio: number,
  chevronRightTriangleWidthToHeightRatio: number
): string => {
  /*  We calculate the approximate heights of both triangles, using the widths of the triangles in columns,
      the angles of the triangles, and some trigonometry.  Once we find which height is larger, we calculate
      that height more specifically using CSS calc and return it as the padding-bottom value.
  */
  const chevronPointPositionFromLeftRatio =
    chevronTipPositionByColumn / totalNumberColumns;
  const leftTriangleRelativeHeight =
    chevronPointPositionFromLeftRatio * chevronLeftTriangleWidthToHeightRatio;
  const rightTriangleRelativeHeight =
    (1 - chevronPointPositionFromLeftRatio) * chevronRightTriangleWidthToHeightRatio;

  const largerRelativeHeight = Math.max(
    leftTriangleRelativeHeight,
    rightTriangleRelativeHeight
  );

  return `${largerRelativeHeight * 100}%;`;
};
